import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Wrench } from "lucide-react";
import { api, type S } from "@/shared/api/client";
import { dateTime } from "@/shared/lib/format";
import { maintenance } from "@/shared/lib/maintenance";
import { Wordmark } from "./Wordmark";

/** The app, or, once the API says ClientOS is closed for maintenance, why and until when. */
export function MaintenanceGate({ children }: { children: ReactNode }) {
  const closed = useSyncExternalStore(maintenance.subscribe, maintenance.closed);
  return closed ? <MaintenanceScreen /> : children;
}

/** Says ClientOS is down for maintenance, asks again every half minute, and comes back by
 * itself when it's over. */
function MaintenanceScreen() {
  const { t, i18n } = useTranslation();
  const status = useQuery({
    queryKey: ["platform-status"], refetchInterval: 30_000,
    queryFn: () => api.get<S["PlatformStatus"]>("/v1/platform/status"),
  });
  const m = status.data?.maintenance;
  useEffect(() => {
    if (m && !m.on) window.location.reload();
  }, [m]);
  const message = m ? (i18n.language === "ar" ? m.message_ar : m.message_en) || m.message_ar || m.message_en : "";
  return (
    <main className="flex min-h-dvh items-center justify-center bg-paper px-4" role="alert">
      <div className="w-full max-w-md text-center">
        <Wordmark className="mx-auto h-8 w-auto" />
        <span className="mx-auto mt-8 flex size-12 items-center justify-center rounded-full bg-saffron-soft text-[#7a5a1c]" aria-hidden>
          <Wrench className="size-6" />
        </span>
        <h1 className="mt-4 text-[20px] font-bold">{t("maintenance.title")}</h1>
        {message && <p className="mt-3 leading-relaxed text-ink-2">{message}</p>}
        {m?.until && <p className="mt-2 text-[13.5px] text-ink-3">{t("maintenance.until", { time: dateTime(m.until) })}</p>}
        <button type="button" className="btn-secondary mt-6" onClick={() => window.location.reload()}>{t("maintenance.retry")}</button>
      </div>
    </main>
  );
}
